// Copyright 2016 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    BusinessUnitDTO,
    CustomerRelationDTO,
    RepresentativeDTO,
    RepresentativeGroupDTO,
} from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { Repository } from "repositories/Repository";

@autoinject
export class BusinessUnitRepository extends Repository {
    private webRequestHandler: IWebRequestHandler;

    // Cache for full BusinessUnit objects
    private businessUnitObjectCache: Map<string, BusinessUnitDTO> = new Map();
    // Cache specifically for business unit names
    private businessUnitNameCache: Map<string, string> = new Map();

    constructor(webRequestHandler: IWebRequestHandler) {
        super();
        this.webRequestHandler = webRequestHandler;
    }

    //#region Get Requests
    public getBusinessUnits(odataQuery: string = undefined): IPagedRequest<BusinessUnitDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<BusinessUnitDTO>>(
            Api.businessUnit + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<BusinessUnitDTO>(request);
    }

    public async getBusinessUnit(id: string): Promise<BusinessUnitDTO> {
        if (this.businessUnitObjectCache.has(id)) {
            return Promise.resolve(this.businessUnitObjectCache.get(id)!);
        }
        const businessUnit = await this.webRequestHandler
            .createGetRequest<BusinessUnitDTO>(Api.businessUnit + "/" + id)
            .send()
            .then((x) => x.content)
            .catch(this.onError);

        if (businessUnit) {
            this.businessUnitObjectCache.set(id, businessUnit);
            // If we fetched the full object, populate the name cache too.
            if (businessUnit.Name) {
                this.businessUnitNameCache.set(id, businessUnit.Name);
            }
        }
        return businessUnit;
    }

    public async getNameForBusinessUnit(id: string): Promise<string> {
        // Check name cache first
        if (this.businessUnitNameCache.has(id)) {
            return Promise.resolve(this.businessUnitNameCache.get(id)!);
        }
        // Check object cache next
        if (this.businessUnitObjectCache.has(id)) {
            return Promise.resolve(this.businessUnitObjectCache.get(id).Name);
        }

        return this.webRequestHandler
            .createGetRequest<string>(Api.businessUnit + "/" + id + "/Name")
            .send()
            .then((x) => {
                this.businessUnitNameCache.set(id, x.content);
                return x.content;
            })
            .catch(this.onError);
    }

    public getDepartmentsForBusinessUnit(id: string, odataQuery: string = undefined): IPagedRequest<BusinessUnitDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<BusinessUnitDTO>>(
            Api.businessUnit + "/" + id + "/Departments" + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<BusinessUnitDTO>(request);
    }

    public getCustomersForBusinessUnit(id: string, odataQuery: string = undefined): IPagedRequest<BusinessUnitDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<BusinessUnitDTO>>(
            Api.businessUnit + "/" + id + "/Customers" + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<BusinessUnitDTO>(request);
    }

    public getServiceProvidersForBusinessUnit(
        id: string,
        odataQuery: string = undefined
    ): IPagedRequest<CustomerRelationDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<CustomerRelationDTO>>(
            Api.businessUnit + "/" + id + "/ServiceProviders" + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<CustomerRelationDTO>(request);
    }

    public getRepresentativesForBusinessUnit(
        id: string,
        odataQuery: string = undefined
    ): IPagedRequest<RepresentativeDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<RepresentativeDTO>>(
            Api.businessUnit + "/" + id + "/Representatives" + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<RepresentativeDTO>(request);
    }

    public getRepresentativeGroupsForBusinessUnit(
        id: string,
        odataQuery: string = undefined
    ): IPagedRequest<RepresentativeGroupDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<RepresentativeGroupDTO>>(
            Api.businessUnit + "/" + id + "/RepresentativeGroups" + (odataQuery ? "?" + odataQuery : "")
        );
        return PagedRequest.create<RepresentativeGroupDTO>(request);
    }
    //#endregion
}
